import { AnyCnameRecord } from "dns"

export type StoreCustomer = {
  id: string
  email: string | null
  default_billing_address_id: string | null
  default_shipping_address_id: string | null
  company_name: string | null
  first_name: string | null
  profile_image?: string
  last_name: string | null
  addresses: StoreCustomerAddress[]
  phone?: string | null
  phone_verified?: number
  created_by?: string | null
  deleted_at?: Date | string | null
  created_at?: Date | string
  updated_at?: Date | string
  customer_type: {
    id: number
    title: string
    dealer: number
    status: number
    description: string
    subscription: number
    highlight_color: string
    balance_conversion: number
    request_invoice_info: number
  }
}

export type BaseUpdateCustomer = {
  company_name?: string
  first_name?: string
  email?: string
  last_name?: string
  phone?: string
  phone_verified?: number
  profile_image?: string
}
export type RegionCountry = {
  region_id: number
  iso_2: string
  iso_3: string
  num_code: string
  name: string
  display_name: string
}
export type StoreUpdateCustomer = BaseUpdateCustomer & {}
export type BaseRegion = {
  id: string
  name: string
  currency_code: string
  automatic_taxes?: boolean
  countries?: RegionCountry[]
  payment_providers?: any[]
  metadata?: Record<string, any> | null
  profile_image?: string | null
  created_at?: string
  updated_at?: string
}
export type StoreRegion = BaseRegion & {
  countries: any
  currencyCode: string
  // Add any additional properties specific to StoreRegion here if needed
}

export type StoreCustomerAddress = {
  id: string
  address_name: string | null
  is_default_shipping?: boolean
  is_default_billing?: boolean
  customer_id?: string
  company: string | null
  first_name: string | null
  last_name: string | null
  address_1: string | null
  address_2: string | null
  city: string | null
  country_code: string | null
  province: string | null
  postal_code: string | null
  phone: string | null
  metadata?: Record<string, unknown> | null
  created_at?: string
  updated_at?: string
  country_id?: number
  city_id?: number
  district_id?: number
}

export type BaseCustomerAddress = {
  id: number
  address_name: string | null
  is_default_shipping?: boolean
  is_default_billing?: boolean
  customer_id?: string
  company: string | null
  first_name: string | null
  last_name: string | null
  address_1: string | null
  address_2: string | null
  city: string | null
  country_code: string | null
  province: string | null
  postal_code: string | null
  phone: string | null
  metadata?: Record<string, unknown> | null
  created_at?: string
  updated_at?: string
}

export type BaseOrderAddress = {
  id: string
  customer_id?: string
  first_name?: string
  last_name?: string
  phone?: string
  company?: string
  address_1?: string
  address_2?: string
  city?: string
  country_code?: string
  province?: string
  postal_code?: string
  metadata: Record<string, unknown> | null
  created_at: Date | string
  updated_at: Date | string
}
export type ProductStatus = "draft" | "proposed" | "published" | "rejected"
export type BaseProduct = {
  id: string
  title: string
  handle: string
  subtitle: string | null
  description: string | null
  is_giftcard: boolean
  status: ProductStatus
  material: string | null
  collection?: any | null
  collection_id: string | null
  categories?: any[] | null
  type?: any | null
  type_id: string | null
  tags?: any[] | null
  variants: BaseProductVariant[] | null
  options: any[] | null
  images: ImageType[]
  discountable: boolean
  external_id: string | null
  created_at: string | null
  updated_at: string | null
  deleted_at: string | null
  metadata?: Record<string, unknown> | null
}
export type BaseProductVariant = {
  id: number
  title: string | null
  sku: string | null
  barcode: string | null
  ean: string | null
  upc: string | null
  allow_backorder: boolean | null
  manage_inventory: boolean | null
  inventory_quantity?: number
  hs_code: string | null
  origin_country: string | null
  mid_code: string | null
  material: string | null
  weight: number | null
  length: number | null
  height: number | null
  width: number | null
  thickness?: number | null
  variant_rank?: number | null
  options: any[] | null
  product?: BaseProduct | null
  product_id?: string
  calculated_price?: any
  created_at: string
  updated_at: string
  deleted_at: string | null
  metadata?: Record<string, unknown> | null
}
export type BaseOrderLineItem = {
  id: string
  title: string
  subtitle: string | null
  thumbnail: string | null
  variant?: BaseProductVariant | null
  variant_id: string | null
  product?: BaseProduct
  product_id: string | null
  product_title: string | null
  product_description: string | null
  product_subtitle: string | null
  product_type: string | null
  product_collection: string | null
  product_handle: string | null
  variant_sku: string | null
  variant_barcode: string | null
  variant_title: string | null
  variant_option_values: Record<string, unknown> | null
  requires_shipping: boolean
  is_discountable: boolean
  is_tax_inclusive: boolean
  compare_at_unit_price?: number
  unit_price: number
  quantity: number
  tax_lines?: any[]
  adjustments?: any[]
  detail: any
  created_at: Date
  updated_at: Date
  metadata: Record<string, unknown> | null
  original_total: number
  original_subtotal: number
  original_tax_total: number
  item_total: number
  item_subtotal: number
  item_tax_total: number
  total: number
  subtotal: number
  tax_total: number
  discount_total: number
  discount_tax_total: number
  refundable_total: number
  refundable_total_per_unit: number
}
export type BaseOrder = {
  id: string
  version: number
  region_id: string | null
  customer_id: string | null
  sales_channel_id: string | null
  email: string | null
  currency_code: string
  display_id?: number
  shipping_address?: BaseOrderAddress | null
  billing_address?: BaseOrderAddress | null
  items: BaseOrderLineItem[] | null
  shipping_methods: any[] | null
  payment_collections?: any[]
  payment_status: any
  fulfillments?: any[]
  fulfillment_status: any
  transactions?: any[]
  summary: any
  metadata?: Record<string, unknown> | null
  created_at: string | Date
  updated_at: string | Date
  original_item_total: number
  original_item_subtotal: number
  original_item_tax_total: number
  item_total: number
  item_subtotal: number
  item_tax_total: number
  original_total: number
  original_subtotal: number
  original_tax_total: number
  total: number
  subtotal: number
  tax_total: number
  discount_total: number
  discount_tax_total: number
  gift_card_total: number
  gift_card_tax_total: number
  shipping_total: number
  shipping_subtotal: number
  shipping_tax_total: number
  original_shipping_total: number
  original_shipping_subtotal: number
  original_shipping_tax_total: number
}
export type StoreOrder = {
  id: string
  shipping_address?: any
  billing_address?: any
  items: any[] | null
  shipping_methods: any[] | null
  payment_collections?: any[] | undefined
  fulfillments?: any[] | undefined
  customer?: StoreCustomer | undefined
  created_at?: string | Date
  total?: number
  currency_code?: string
}

export type BaseCollection = {
  id: string
  title: string
  handle: string
  created_at: string
  updated_at: string
  deleted_at: string | null
  products?: BaseProduct[]
  metadata: Record<string, unknown> | null
}
export type StoreCollection = Omit<BaseCollection, "products"> & {
  products?: StoreProduct[]
}
export type BaseProductCategory = {
  id: string
  name: string
  description: string
  handle: string
  is_active: boolean
  is_internal: boolean
  rank: number | null
  parent_category_id: string | null
  parent_category: BaseProductCategory | null
  category_children: BaseProductCategory[]
  products?: BaseProduct[]
  metadata?: Record<string, unknown> | null
  created_at: string
  updated_at: string
  deleted_at: string | null
}
export type StoreProductCategory = Omit<
  BaseProductCategory,
  | "is_active"
  | "is_internal"
  | "products"
  | "parent_category"
  | "category_children"
> & {
  products?: StoreProduct[]
  parent_category: StoreProductCategory | null
  category_children: StoreProductCategory[]
  parent_categories?: StoreProductCategory[]
  child_categories?: StoreProductCategory[]
}
export type StoreProductVariant = Omit<BaseProductVariant, "product" | "options"> & {
  id: string
  options: any[] | null
  product?: StoreProduct | null
  sku?: string | undefined
  barcode?: string | undefined
  quantity: number
  inventory_quantity: number
  show_category_products_banner: number
  width: number | null
  length: number | null
  thickness: number | null
  stock_type: string
  fulfilment_type: string
  price: number
  unitPrice: any
  sales_price: number
  market_price: number
  is_favorite?: boolean
  favorite_id?: string
  stocks?:
    | {
        id: number
        status: string
        sales_price: number
        market_price: number
      }[]
    | null
  form_questions?: any[] | null
  images:
    | {
        id: number
        image_key: string
        is_thumbnail: number
        order_index: number
      }[]
    | null
}
export type BaseProductType = {
  id: string
  value: string
  created_at: string
  updated_at: string
  deleted_at?: string | null
  metadata?: Record<string, unknown> | null
}
export type StoreProductType = BaseProductType & {}
export type StoreProduct = Omit<
  BaseProduct,
  "categories" | "sales_channels" | "variants" | "options" | "collection"
> & {
  collection?: StoreCollection | null
  categories?: StoreProductCategory[] | null
  variants: StoreProductVariant[] | null
  type?: StoreProductType | null
  unit_price_type_id?: number | null
  tags?: any[] | null
  options: any[] | null
  images: ImageType[]
  platforms?: string
  age_list?: string
  region_list?: string
  product_info?: string | null
  average_rating?: number
  unit_price_type_name?: string | null
  include_in_calculation_helper?: number
}

export type StoreCartItem = {
  item_id: number
  player_id: number
  matchingIds?: number[]
  product: BaseProduct
  variant: StoreProductVariant
  cart: {
    id: string
  }
  tax_lines: any
  adjustments: [
    {
      amount: any
      description: string
    }
  ]
  created_at?: Date
  updated_at?: Date
  deleted_at?: Date
}
export type StoreCart = {
  id: number
  currencyCode: string
  email: string
  createdAt: string
  updatedAt: string | null
  completedAt: string | null
  customerId: number
  createdBy: number
  total: any
  totalAfterDiscount?: number
  itemTotal: any
  itemSubtotal: any
  itemTaxTotal: number
  totalDiscountAmount?: number
  itemPromotionDiscount?: number
  totalPaidAmount?: number
  walletPaymentAmount: number
  remainingToPay: number
  items: StoreCartItem[]
  region?: StoreRegion
  shippingTotal?: number
  shippingAdjustments?: number
  priceValidation?: PriceValidation
}
export type PriceChangeNotice = {
  type: string
  message: string
  lineItemId: number
  productId: number
  productName: string
  variantTitle: string
  oldPrice: number
  newPrice: number
  priceChange: number
  priceChangePercent: number
  changeDirection: "increased" | "decreased"
}

export type PriceUpdate = {
  lineItemId: number
  variantId: number
  productId: number
  productName: string
  variantTitle: string
  oldPrice: number
  newPrice: number
  priceDifference: number
  priceChangePercent: number
}

export type PriceValidation = {
  hasPartialPayment: boolean
  hasPriceChanges: boolean
  notices: PriceChangeNotice[]
  priceUpdates: PriceUpdate[]
}

export type SelectParams = {
  fields?: string
}
export type FindParams = SelectParams & {
  limit?: number

  offset?: number

  order?: string

  collection_id?: string | string[]

  category_id?: string | string[]

  id?: string | string[]
}
export type AdminBatchLink = {
  add?: string[]
  remove?: string[]
}

export type BaseCartLineItemTotals = {
  original_total: number
  original_subtotal: number
  original_tax_total: number
  item_total: number
  item_subtotal: number
  item_tax_total: number
  total: number
  subtotal: number
  tax_total: number
  discount_total: number
  discount_tax_total: number
}
export type BaseCartLineItem = BaseCartLineItemTotals & {
  id: string
  title: string
  subtitle?: string
  thumbnail?: string
  quantity: number
  product?: BaseProduct
  product_id?: string
  product_title?: string
  product_description?: string
  product_subtitle?: string
  product_type?: string
  product_collection?: string
  product_handle?: string
  variant?: BaseProductVariant
  variant_id?: string
  variant_sku?: string
  variant_barcode?: string
  variant_title?: string
  variant_option_values?: Record<string, unknown>
  requires_shipping: boolean
  is_discountable: boolean
  is_tax_inclusive: boolean
  compare_at_unit_price?: number
  unit_price: number
  tax_lines?: AnyCnameRecord[]
  adjustments?: any[]
  cart: any
  cart_id: number
  metadata?: Record<string, unknown> | null
  created_at?: Date
  updated_at?: Date
  deleted_at?: Date
}
export type StoreCartLineItem = Omit<
  BaseCartLineItem,
  "product" | "variant" | "cart"
> & {
  product?: StoreProduct
  variant?: StoreProductVariant
  cart: StoreCart
  tax_lines?: any[]
  adjustments?: any[]
}

export type StoreProductCategoryPage = {
  id: number
  name: string
  description: string
  handle: string
  mpath: string
  is_active: number
  is_internal: number
  category_rank: number
  parent_category_id: number | null
  logo_image: string
  background_image: string
  character_image: string
  created_at: string
  updated_at: string
  deleted_at: string | null
  parent_categories?: StoreProductCategoryPage[]
  child_categories?: StoreProductCategoryPage[]
}

export type StoreProductReview = {
  metadata: any
  data: {
    images: ImageType[]
    title: string
    id: number
    name: string
    region_id: number
    variant_id: number
    status: string
    rating: number
    comment: string
    customer_id: number
    customer_name: string
    customer_last_name: string
    created_at: string
  }[]
}
export type BaseCartAddress = {
  id: string
  customer_id?: string
  first_name?: string
  last_name?: string
  phone?: string
  company?: string
  address_1?: string
  address_2?: string
  city?: string
  country_code?: string
  province?: string
  postal_code?: string
  metadata?: Record<string, unknown> | null
  created_at: Date | string
  updated_at: Date | string
}

export enum PromotionType {
  AmountOffOrder = "Amount Off Order",
  AmountOffProduct = "Amount Off Product",
  PercentageOffOrder = "Percentage Off Order",
  PercentageOffProduct = "Percentage Off Product",
}

export type StorePromotion = {
  id: string
  code: string
  promotion_value: number
  valid_product_ids: string
  type: PromotionType
  is_active: string
  is_featured: string
  feature_description: string
  minimum_order_value: number
}

export type StoreCartAddress = BaseCartAddress & {}

export type ImageType = {
  id: number
  image_key: string
  is_thumbnail: number
  order_index: number
}

export type StoreFavorite = {
  id: number
  customer_id: number
  product_id: number
  product_variant_id: number // The ID of the variant that was favorited
  created_at: string
  updated_at: string
  customer_first_name: string
  customer_last_name: string
  customer_email: string
  product_variant_title: string // Product variant title
  product_variant_price: number // Product variant price
  product_variant_image_key: string // Product variant image key
  product_image_key: string // Product image key
  product_title: string // Product title
  product_handle: string // Product handle
  product?: StoreProduct // Assuming this could be populated for richer display
  has_active_stocks?: number
  sales_price?: number
  market_price?: number
  display_price?: number
}
